import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import styles from './ControleContatoClientes.module.css';

const INATIVIDADE_OPTIONS = [
    { label: 'Todos', value: '' },
    { label: '1 Mês (31+ dias)', value: '31' },
    { label: '2 Meses (61+ dias)', value: '61' },
    { label: '3 Meses (91+ dias)', value: '91' },
    { label: '6 Meses (181+ dias)', value: '181' },
    { label: '1 Ano (365+ dias)', value: '365' }
];

const ORDENAR_OPTIONS = [
    { label: 'Nome', value: 'nome', dir: 'asc' },
    { label: 'Dias sem comprar', value: 'dias_sem_comprar', dir: 'asc' },
    { label: 'Último pedido (recente)', value: 'data_ultimo_pedido', dir: 'desc' },
    { label: 'Maior valor', value: 'valor_ultimo_pedido', dir: 'desc' },
    { label: 'Mais compras', value: 'qtd_compras', dir: 'desc' },
    { label: 'Cidade', value: 'cidade', dir: 'asc' }
];

const LIMIT_OPTIONS = [20, 50, 100, 200];

const ControleContatoClientes = () => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);
    const [sortConfig, setSortConfig] = useState({ key: 'nome', direction: 'asc' });
    const [copiedId, setCopiedId] = useState(null);

    // Filters
    const [inativosHa, setInativosHa] = useState('');
    const [cidade, setCidade] = useState('Todas');
    const [valorMin, setValorMin] = useState('');
    const [valorMax, setValorMax] = useState('');
    const [ordenar, setOrdenar] = useState('nome');
    const [cancelados, setCancelados] = useState(false);

    // Observações modal (timeline acumulativa)
    const [obsModal, setObsModal] = useState(null);
    const [obsText, setObsText] = useState('');
    const [obsSaving, setObsSaving] = useState(false);
    const [obsLoading, setObsLoading] = useState(false);
    const [obsList, setObsList] = useState([]);

    // Histórico modal
    const [histModal, setHistModal] = useState(null);
    const [histData, setHistData] = useState(null);
    const [histLoading, setHistLoading] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, inativosHa, cidade, valorMin, valorMax, ordenar, limit, cancelados]);

    const fetchClientes = async () => {
        const params = {
            page,
            limit,
            sortBy: sortConfig.key,
            order: sortConfig.direction
        };
        if (debouncedSearch) params.busca = debouncedSearch;
        if (inativosHa) params.inativos_ha = inativosHa;
        if (cidade && cidade !== 'Todas') params.cidade = cidade;
        if (valorMin) params.valor_min = valorMin;
        if (valorMax) params.valor_max = valorMax;
        if (cancelados) params.cancelados = 'true';

        const response = await api.get('/api/clientes/controle-contato', { params });
        return response.data;
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: ['controle-contato', page, debouncedSearch, sortConfig, inativosHa, cidade, valorMin, valorMax, limit, cancelados],
        queryFn: fetchClientes,
        keepPreviousData: true
    });

    const handleOrdenarChange = (value) => {
        setOrdenar(value);
        const opt = ORDENAR_OPTIONS.find(o => o.value === value);
        if (opt) {
            setSortConfig({ key: opt.value, direction: opt.dir });
        }
    };

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : (key === 'total_pedidos' || key === 'qtd_compras' || key === 'valor_ultimo_pedido' ? 'desc' : 'asc')
        }));
        setPage(1);
    };

    const isCelular = (phone) => {
        if (!phone) return false;
        const clean = phone.replace(/\D/g, '');
        return clean.length === 11 && clean[2] === '9';
    };

    const openWhatsApp = (phone) => {
        if (!phone) return;
        const clean = phone.replace(/\D/g, '');
        if (clean.length === 11 && clean[2] === '9') {
            window.open(`https://wa.me/55${clean}`, '_blank');
        }
    };

    const copyToClipboard = useCallback((text, id) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    }, []);

    // === Observações (Timeline Acumulativa) ===
    const loadObservacoes = async (clienteId) => {
        try {
            const res = await api.get(`/api/clientes/observacoes/${clienteId}`);
            setObsList(res.data?.data || []);
        } catch (e) {
            console.error('Erro ao carregar observações:', e);
            setObsList([]);
        }
    };

    const openObsModal = async (clienteId, nome) => {
        setObsModal({ clienteId, nome });
        setObsText('');
        setObsList([]);
        setObsLoading(true);
        await loadObservacoes(clienteId);
        setObsLoading(false);
    };

    const saveObservacao = async () => {
        if (!obsModal || !obsText.trim()) return;
        setObsSaving(true);
        try {
            const res = await api.post(`/api/clientes/observacoes/${obsModal.clienteId}`, {
                observacao: obsText
            });
            if (res.data?.data) {
                setObsList(prev => [res.data.data, ...prev]);
            } else {
                await loadObservacoes(obsModal.clienteId);
            }
            setObsText('');
        } catch (e) {
            console.error('Erro ao salvar observação:', e);
            alert('Erro ao salvar observação');
        }
        setObsSaving(false);
    };

    const deleteObservacao = async (obsId) => {
        if (!confirm('Deseja realmente excluir esta observação?')) return;
        try {
            await api.delete(`/api/clientes/observacoes/${obsId}`);
            setObsList(prev => prev.filter(o => o.id !== obsId));
        } catch (e) {
            console.error('Erro ao excluir observação:', e);
            if (e.response?.status === 403) {
                alert('Sem permissão para excluir esta observação');
            } else {
                alert('Erro ao excluir observação');
            }
        }
    };

    const formatObsDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const d = new Date(dateStr + 'Z');
            return d.toLocaleDateString('pt-BR', {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        } catch { return dateStr; }
    };

    // === Histórico Completo ===
    const openHistModal = async (clienteId, nome, telefone) => {
        setHistModal({ clienteId, nome, telefone });
        setHistData(null);
        setHistLoading(true);
        try {
            const res = await api.get(`/api/clientes/historico-completo/${clienteId}`);
            setHistData(res.data);
        } catch (e) {
            console.error('Erro ao carregar histórico:', e);
        }
        setHistLoading(false);
    };

    const [exporting, setExporting] = useState(false);

    const handleExportCSV = async () => {
        if (!data?.pagination?.total || data.pagination.total === 0) {
            alert('Nenhum cliente para exportar.');
            return;
        }

        setExporting(true);
        try {
            // Buscar TODOS os clientes filtrados (sem limite de paginação)
            const params = {
                page: 1,
                limit: 100000,
                sortBy: sortConfig.key,
                order: sortConfig.direction
            };
            if (debouncedSearch) params.busca = debouncedSearch;
            if (inativosHa) params.inativos_ha = inativosHa;
            if (cidade && cidade !== 'Todas') params.cidade = cidade;
            if (valorMin) params.valor_min = valorMin;
            if (valorMax) params.valor_max = valorMax;
            if (cancelados) params.cancelados = 'true';

            const response = await api.get('/api/clientes/controle-contato', { params });
            const todosClientes = response.data?.data || [];

            if (todosClientes.length === 0) {
                alert('Nenhum cliente encontrado com os filtros atuais.');
                setExporting(false);
                return;
            }

            const headers = ['NOME', 'CPF/CNPJ', 'TELEFONE', 'EMAIL', 'CIDADE', 'UF', 'TOTAL PEDIDOS', 'QTD COMPRAS', 'CANCELADOS', 'DATA ÚLTIMO PEDIDO', 'VALOR ÚLTIMO PEDIDO'];
            const csvRows = [
                headers.join(';'),
                ...todosClientes.map(c => [
                    (c.nome || c.fantasia || '').replace(/;/g, ','),
                    c.cpf_cnpj || '',
                    c.telefone || '',
                    c.email || '',
                    c.cidade || '',
                    c.uf || '',
                    c.total_pedidos || 0,
                    c.qtd_compras || 0,
                    c.qtd_cancelados || 0,
                    c.data_ultimo_pedido ? new Date(c.data_ultimo_pedido).toLocaleDateString('pt-BR') : '',
                    c.valor_ultimo_pedido ? parseFloat(c.valor_ultimo_pedido).toFixed(2).replace('.', ',') : '0,00'
                ].join(';'))
            ];
            const csvContent = '\uFEFF' + csvRows.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `controle_contato_clientes_${new Date().toISOString().split('T')[0]}.csv`;
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Erro ao exportar:', error);
            alert('Erro ao exportar clientes. Tente novamente.');
        }
        setExporting(false);
    };

    const formatCurrency = (value) => {
        if (!value && value !== 0) return '-';
        return `R$ ${parseFloat(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('pt-BR');
        } catch { return '-'; }
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return <span className={styles.sortArrow}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
    };

    const handleClearFilters = () => {
        setInativosHa('');
        setCidade('Todas');
        setValorMin('');
        setValorMax('');
        setOrdenar('nome');
        setSearch('');
        setSortConfig({ key: 'nome', direction: 'asc' });
        setLimit(100);
        setCancelados(false);
    };

    const cidadesList = data?.cidades || [];

    return (
        <div className={styles.container}>
            <h2 className={styles.pageTitle}>Controle Contato Clientes</h2>

            {/* Filter Bar */}
            <div className={styles.filterBar}>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>🕐 INATIVOS HÁ</label>
                    <select className={styles.filterSelect} value={inativosHa} onChange={(e) => setInativosHa(e.target.value)}>
                        {INATIVIDADE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>📍 CIDADE</label>
                    <select className={styles.filterSelect} value={cidade} onChange={(e) => setCidade(e.target.value)}>
                        <option value="Todas">Todas</option>
                        {cidadesList.map(c => (
                            <option key={c} value={c}>{c}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>💰 VALOR MIN</label>
                    <input type="number" className={styles.filterInput} placeholder="R$ 0" value={valorMin} onChange={(e) => setValorMin(e.target.value)} min="0" step="10" />
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>💰 VALOR MAX</label>
                    <input type="number" className={styles.filterInput} placeholder="R$ ∞" value={valorMax} onChange={(e) => setValorMax(e.target.value)} min="0" step="10" />
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>↕️ ORDENAR</label>
                    <select className={styles.filterSelect} value={ordenar} onChange={(e) => handleOrdenarChange(e.target.value)}>
                        {ORDENAR_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>📄 PAGINAÇÃO</label>
                    <select className={styles.filterSelect} value={limit} onChange={(e) => setLimit(parseInt(e.target.value))}>
                        {LIMIT_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>🔍 BUSCA</label>
                    <input type="text" className={styles.filterInput} placeholder="nome, e-mail, telefone..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className={styles.filterActions}>
                    <button
                        className={`${styles.filterToggleBtn} ${cancelados ? styles.filterToggleActive : ''}`}
                        onClick={() => setCancelados(!cancelados)}
                        title={cancelados ? 'Mostrando apenas com cancelados' : 'Mostrar apenas com cancelados'}
                    >
                        ❌ Cancelados
                    </button>
                    <button className={styles.filterSearchBtn} onClick={() => setDebouncedSearch(search)} title="Buscar">🔍</button>
                    <button className={styles.filterClearBtn} onClick={handleClearFilters} title="Limpar filtros">✕</button>
                </div>
            </div>

            {/* Export bar */}
            <div className={styles.toolbar}>
                <button className={styles.exportBtn} onClick={handleExportCSV} disabled={exporting}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {exporting ? 'Exportando...' : `Exportar CSV (${data?.pagination?.total?.toLocaleString('pt-BR') || 0} clientes)`}
                </button>
            </div>

            {/* Table */}
            {isLoading ? (
                <div className={styles.loadingState}>
                    <div className={styles.spinner}></div>
                    <span>Carregando clientes...</span>
                </div>
            ) : isError ? (
                <div className={styles.errorState}>
                    <span>❌ Erro ao carregar dados. Tente novamente.</span>
                </div>
            ) : (
                <>
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('nome')} className={styles.sortable}>Nome {renderSortIcon('nome')}</th>
                                    <th onClick={() => handleSort('cpf_cnpj')} className={styles.sortable}>CPF / CNPJ {renderSortIcon('cpf_cnpj')}</th>
                                    <th onClick={() => handleSort('telefone')} className={styles.sortable}>Telefone {renderSortIcon('telefone')}</th>
                                    <th onClick={() => handleSort('email')} className={styles.sortable}>Email {renderSortIcon('email')}</th>
                                    <th onClick={() => handleSort('cidade')} className={styles.sortable}>Localidade {renderSortIcon('cidade')}</th>
                                    <th onClick={() => handleSort('total_pedidos')} className={styles.sortable}>Total Pedidos {renderSortIcon('total_pedidos')}</th>
                                    <th onClick={() => handleSort('qtd_compras')} className={styles.sortable}>Qtd Compras {renderSortIcon('qtd_compras')}</th>
                                    <th onClick={() => handleSort('qtd_cancelados')} className={styles.sortable}>Cancelados {renderSortIcon('qtd_cancelados')}</th>
                                    <th onClick={() => handleSort('data_ultimo_pedido')} className={styles.sortable}>Último Pedido {renderSortIcon('data_ultimo_pedido')}</th>
                                    <th onClick={() => handleSort('valor_ultimo_pedido')} className={styles.sortable}>Valor Último {renderSortIcon('valor_ultimo_pedido')}</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.data?.length > 0 ? data.data.map((c, idx) => (
                                    <tr key={c.codigo_cliente || idx}>
                                        <td className={styles.clientName}>{c.nome || c.fantasia || '-'}</td>
                                        <td className={styles.cpfCell}>{c.cpf_cnpj || '-'}</td>
                                        <td>
                                            <span
                                                className={isCelular(c.telefone) ? styles.phoneWhatsapp : styles.phoneNormal}
                                                onClick={() => isCelular(c.telefone) && openWhatsApp(c.telefone)}
                                                title={isCelular(c.telefone) ? 'Abrir WhatsApp' : ''}
                                            >
                                                {isCelular(c.telefone) && <span className={styles.waIcon}>📱</span>}
                                                {c.telefone || '-'}
                                            </span>
                                        </td>
                                        <td className={styles.emailCell}>
                                            {c.email ? (
                                                <span className={styles.emailWrapper}>
                                                    <span className={styles.emailText}>{c.email}</span>
                                                    <button
                                                        className={`${styles.copyBtn} ${copiedId === `email-${c.codigo_cliente}` ? styles.copied : ''}`}
                                                        onClick={() => copyToClipboard(c.email, `email-${c.codigo_cliente}`)}
                                                        title="Copiar email"
                                                    >
                                                        {copiedId === `email-${c.codigo_cliente}` ? '✓' : '📋'}
                                                    </button>
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className={styles.locationCell}>
                                            {c.cidade ? `${c.cidade}/${c.uf || ''}` : '-'}
                                        </td>
                                        <td className={styles.numberCell}>
                                            <span
                                                className={`${styles.badge} ${(c.total_pedidos || 0) > 0 ? styles.badgeActive : styles.badgeZero} ${(c.total_pedidos || 0) > 0 ? styles.badgeClickable : ''}`}
                                                onClick={() => (c.total_pedidos || 0) > 0 && openHistModal(c.codigo_cliente, c.nome || c.fantasia || 'Cliente', c.telefone)}
                                                title={(c.total_pedidos || 0) > 0 ? 'Ver histórico de pedidos' : ''}
                                            >
                                                {c.total_pedidos || 0}
                                            </span>
                                        </td>
                                        <td className={styles.numberCell}>
                                            <span className={`${styles.badge} ${(c.qtd_compras || 0) > 0 ? styles.badgeActive : styles.badgeZero}`}>
                                                {c.qtd_compras || 0}
                                            </span>
                                        </td>
                                        <td className={styles.numberCell}>
                                            <span className={`${styles.badge} ${(c.qtd_cancelados || 0) > 0 ? styles.badgeCancelado : styles.badgeZero}`}>
                                                {c.qtd_cancelados || 0}
                                            </span>
                                        </td>
                                        <td className={styles.dateCell}>{formatDate(c.data_ultimo_pedido)}</td>
                                        <td className={styles.valueCell}>{formatCurrency(c.valor_ultimo_pedido)}</td>
                                        <td className={styles.actionsCell}>
                                            {isCelular(c.telefone) && (
                                                <button className={styles.actionBtn} onClick={() => openWhatsApp(c.telefone)} title="WhatsApp">💬</button>
                                            )}
                                            {c.email && (
                                                <button
                                                    className={styles.actionBtn}
                                                    onClick={() => copyToClipboard(c.email, `action-${c.codigo_cliente}`)}
                                                    title={copiedId === `action-${c.codigo_cliente}` ? 'Copiado!' : 'Copiar email'}
                                                >
                                                    {copiedId === `action-${c.codigo_cliente}` ? '✅' : '📧'}
                                                </button>
                                            )}
                                            <button
                                                className={styles.actionBtn}
                                                onClick={() => openObsModal(c.codigo_cliente, c.nome || c.fantasia || 'Cliente')}
                                                title="Observações"
                                            >
                                                📝
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="11" className={styles.emptyState}>
                                            {debouncedSearch ? `Nenhum cliente encontrado para "${debouncedSearch}"` : 'Nenhum cliente cadastrado'}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {data?.pagination && data.pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button disabled={page === 1} onClick={() => setPage(1)} className={styles.pageBtn}>⏮</button>
                            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className={styles.pageBtn}>← Anterior</button>
                            <span className={styles.pageInfo}>
                                Página <strong>{page}</strong> de <strong>{data.pagination.totalPages}</strong>
                                <span className={styles.pageSep}>|</span>
                                Total: <strong>{data.pagination.total.toLocaleString('pt-BR')}</strong> clientes
                            </span>
                            <button disabled={page >= data.pagination.totalPages} onClick={() => setPage(p => p + 1)} className={styles.pageBtn}>Próximo →</button>
                            <button disabled={page >= data.pagination.totalPages} onClick={() => setPage(data.pagination.totalPages)} className={styles.pageBtn}>⏭</button>
                        </div>
                    )}
                </>
            )}

            {/* Modal de Observações (Timeline Acumulativa) */}
            {obsModal && (
                <div className={styles.modalOverlay} onClick={() => setObsModal(null)}>
                    <div className={`${styles.modalContent} ${styles.obsModalContent}`} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h3>📝 Observações</h3>
                                <span className={styles.obsSubtitle}>Histórico de anotações do cliente</span>
                            </div>
                            <button className={styles.modalClose} onClick={() => setObsModal(null)}>✕</button>
                        </div>
                        <div className={styles.modalClientName}>
                            {obsModal.nome}
                        </div>

                        {/* Área para adicionar nova observação */}
                        <div className={styles.obsAddSection}>
                            <div className={styles.obsAddLabel}>✏️ Nova observação</div>
                            <textarea
                                className={styles.obsTextarea}
                                value={obsText}
                                onChange={e => setObsText(e.target.value)}
                                placeholder="Digite sua observação sobre este cliente..."
                                rows={3}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                        saveObservacao();
                                    }
                                }}
                            />
                            <div className={styles.obsAddActions}>
                                <span className={styles.obsHint}>Ctrl+Enter para salvar</span>
                                <button
                                    className={styles.obsAddBtn}
                                    onClick={saveObservacao}
                                    disabled={obsSaving || !obsText.trim()}
                                >
                                    {obsSaving ? '⏳ Salvando...' : '➕ Adicionar'}
                                </button>
                            </div>
                        </div>

                        {/* Timeline de observações */}
                        <div className={styles.obsTimeline}>
                            {obsLoading ? (
                                <div className={styles.obsTimelineLoading}>
                                    <div className={styles.spinner}></div>
                                    <span>Carregando observações...</span>
                                </div>
                            ) : obsList.length > 0 ? (
                                obsList.map((obs) => (
                                    <div key={obs.id} className={styles.obsItem}>
                                        <div className={styles.obsItemHeader}>
                                            <div className={styles.obsAutorInfo}>
                                                <span className={styles.obsAutorAvatar}>
                                                    {(obs.autor_nome || 'S')[0].toUpperCase()}
                                                </span>
                                                <div>
                                                    <span className={styles.obsAutorNome}>{obs.autor_nome || 'Sistema'}</span>
                                                    <span className={styles.obsItemDate}>{formatObsDate(obs.created_at)}</span>
                                                </div>
                                            </div>
                                            <button
                                                className={styles.obsDeleteBtn}
                                                onClick={() => deleteObservacao(obs.id)}
                                                title="Excluir observação"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                        <div className={styles.obsItemText}>{obs.observacao}</div>
                                    </div>
                                ))
                            ) : (
                                <div className={styles.obsEmpty}>
                                    <span className={styles.obsEmptyIcon}>📋</span>
                                    <span>Nenhuma observação registrada ainda.</span>
                                    <span className={styles.obsEmptyHint}>Adicione a primeira observação acima.</span>
                                </div>
                            )}
                        </div>

                        <div className={styles.modalActions}>
                            <span className={styles.obsCount}>
                                {obsList.length} {obsList.length === 1 ? 'observação' : 'observações'}
                            </span>
                            <button className={styles.modalCancelBtn} onClick={() => setObsModal(null)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Histórico Completo */}
            {histModal && (
                <div className={styles.modalOverlay} onClick={() => setHistModal(null)}>
                    <div className={`${styles.modalContent} ${styles.histModalContent}`} onClick={e => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h3>{histModal.nome}</h3>
                                <span className={styles.histSubtitle}>Histórico Completo do Cliente</span>
                            </div>
                            <button className={styles.modalClose} onClick={() => setHistModal(null)}>✕</button>
                        </div>

                        {histLoading ? (
                            <div className={styles.modalLoading}>Carregando histórico...</div>
                        ) : histData ? (
                            <>
                                {/* KPI Cards */}
                                <div className={styles.histKpiRow}>
                                    <div className={styles.histKpiCard}>
                                        <div className={styles.histKpiLabel}>Total Pedidos</div>
                                        <div className={styles.histKpiValue}>{histData.kpis.total_pedidos}</div>
                                    </div>
                                    <div className={`${styles.histKpiCard} ${styles.histKpiGreen}`}>
                                        <div className={styles.histKpiLabel}>Total Gasto (LTV)</div>
                                        <div className={styles.histKpiValue}>{formatCurrency(histData.kpis.total_gasto)}</div>
                                    </div>
                                    <div className={`${styles.histKpiCard} ${styles.histKpiTeal}`}>
                                        <div className={styles.histKpiLabel}>Ticket Médio</div>
                                        <div className={styles.histKpiValue}>{formatCurrency(histData.kpis.ticket_medio)}</div>
                                    </div>
                                    <div className={styles.histKpiCard}>
                                        <div className={styles.histKpiLabel}>Primeira Compra</div>
                                        <div className={styles.histKpiValue} style={{ fontSize: '1rem' }}>{formatDate(histData.kpis.primeira_compra)}</div>
                                        <div className={styles.histKpiSub}>Última: {formatDate(histData.kpis.ultima_compra)}</div>
                                    </div>
                                </div>

                                {/* Orders only - full width */}
                                <div className={styles.histPedidos}>
                                    <h4>📦 Histórico de Pedidos</h4>
                                    <table className={styles.histPedTable}>
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Número</th>
                                                <th>Status</th>
                                                <th>Itens</th>
                                                <th>Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {histData.pedidos?.length > 0 ? histData.pedidos.map((p, i) => (
                                                <tr key={i}>
                                                    <td>{formatDate(p.data_venda)}</td>
                                                    <td><span className={styles.pedidoNum}>#{p.numero}</span></td>
                                                    <td><span className={`${styles.statusBadge} ${p.status === 'Cancelado' ? styles.statusCancelado : styles.statusOk}`}>{p.status || 'Consolidado'}</span></td>
                                                    <td><span className={styles.itensBadge}>{p.itens_count} {p.itens_count === 1 ? 'item' : 'itens'}</span></td>
                                                    <td className={styles.pedidoValor}>{formatCurrency(p.valor_total)}</td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="5" style={{ textAlign: 'center', color: '#9ca3af', padding: '20px' }}>Nenhum pedido encontrado</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className={styles.modalLoading}>Erro ao carregar dados</div>
                        )}

                        <div className={styles.histFooter}>
                            <button className={styles.modalCancelBtn} onClick={() => setHistModal(null)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ControleContatoClientes;
